// Real state/city numeric-code dataset for the ICICI CBDC Create/Update
// Rule API's `location` field: `[{ city, state, bufferZone }]`.
//
// Sourced from the official ICICI CBDC state/city reference data confirmed
// by the bank. `code` for each state is ICICI's `stateId` and `code` for
// each city is ICICI's `cityId` - these are the exact numeric values ICICI
// expects in the Create/Update Rule API payload.
//
// A handful of cities in the source data were duplicated under the same
// state with two different cityIds (Bangalore, Hyderabad, Baudh) - the
// lower cityId was kept as canonical in each case since a dropdown cannot
// meaningfully offer the same city name twice.

export interface LocationCity {
  name: string
  code: string
}

export interface LocationState {
  name: string
  code: string
  cities: LocationCity[]
}

export const LOCATION_STATES: LocationState[] = [
  {
    name: "Andaman & Nicobar",
    code: "1",
    cities: [{ name: "Andamans", code: "1" }],
  },
  {
    name: "Andhra Pradesh",
    code: "2",
    cities: [
      { name: "Adilabad", code: "2" },
      { name: "Anantapur", code: "3" },
      { name: "Chittoor", code: "4" },
      { name: "Cuddapah", code: "5" },
      { name: "East Godavari", code: "6" },
      { name: "Guntur", code: "7" },
      { name: "Hyderabad", code: "8" },
      { name: "Karimnagar", code: "10" },
      { name: "Khammam", code: "11" },
      { name: "Krishna", code: "12" },
      { name: "Kurnool", code: "13" },
      { name: "Mahbubnagar", code: "14" },
      { name: "Medak", code: "15" },
      { name: "Nalgonda", code: "16" },
      { name: "Nellore", code: "17" },
      { name: "Nizamabad", code: "18" },
      { name: "Prakasam", code: "19" },
      { name: "Srikakulam", code: "20" },
      { name: "Visakhapatnam", code: "21" },
      { name: "Vizianagaram", code: "22" },
      { name: "Warangal", code: "23" },
      { name: "West Godavari", code: "24" },
    ],
  },
  {
    name: "Arunachal Pradesh",
    code: "3",
    cities: [
      { name: "Anjaw", code: "25" },
      { name: "Changlang", code: "26" },
      { name: "Dibang Valley", code: "27" },
      { name: "East Kameng", code: "28" },
      { name: "East Siang", code: "29" },
      { name: "Kurung Kumey", code: "30" },
      { name: "Lohit", code: "31" },
      { name: "Lower Dibang Valley", code: "32" },
      { name: "Lower Subansiri", code: "33" },
      { name: "Papum Pare", code: "34" },
      { name: "Tawang", code: "35" },
      { name: "Tirap", code: "36" },
      { name: "Upper Siang", code: "37" },
      { name: "Upper Subansiri", code: "38" },
      { name: "West Kameng", code: "39" },
      { name: "West Siang", code: "40" },
    ],
  },
  {
    name: "Assam",
    code: "4",
    cities: [
      { name: "Barpeta", code: "41" },
      { name: "Bongaigaon", code: "42" },
      { name: "Cachar", code: "43" },
      { name: "Darrang", code: "44" },
      { name: "Dhemaji", code: "45" },
      { name: "Dhubri", code: "46" },
      { name: "Dibrugarh", code: "47" },
      { name: "Goalpara", code: "48" },
      { name: "Golaghat", code: "49" },
      { name: "Hailakandi", code: "50" },
      { name: "Jorhat", code: "51" },
      { name: "Kamrup", code: "52" },
      { name: "Karbi Anglong", code: "53" },
      { name: "Karimganj", code: "54" },
      { name: "Kokrajhar", code: "55" },
      { name: "Lakhimpur", code: "56" },
      { name: "Marigaon", code: "57" },
      { name: "Nagaon", code: "58" },
      { name: "Nalbari", code: "59" },
      { name: "North Cachar Hills", code: "60" },
      { name: "Sibsagar", code: "61" },
      { name: "Sonitpur", code: "62" },
      { name: "Tinsukia", code: "63" },
    ],
  },
  {
    name: "Bihar",
    code: "5",
    cities: [
      { name: "Araria", code: "64" },
      { name: "Aurangabad", code: "65" },
      { name: "Banka", code: "66" },
      { name: "Begusarai", code: "67" },
      { name: "Bhagalpur", code: "68" },
      { name: "Bhojpur", code: "69" },
      { name: "Buxar", code: "70" },
      { name: "Darbhanga", code: "71" },
      { name: "Gaya", code: "72" },
      { name: "Gopalganj", code: "73" },
      { name: "Jamui", code: "74" },
      { name: "Jehanabad", code: "75" },
      { name: "Kaimur (Bhabua)", code: "76" },
      { name: "Katihar", code: "77" },
      { name: "Khagaria", code: "78" },
      { name: "Kishanganj", code: "79" },
      { name: "Lakhisarai", code: "80" },
      { name: "Madhepura", code: "81" },
      { name: "Madhubani", code: "82" },
      { name: "Munger", code: "83" },
      { name: "Muzaffarpur", code: "84" },
      { name: "Nalanda", code: "85" },
      { name: "Nawada", code: "86" },
      { name: "Pashchim Champaran", code: "87" },
      { name: "Patna", code: "88" },
      { name: "Purba Champaran", code: "89" },
      { name: "Purnia", code: "90" },
      { name: "Rohtas", code: "91" },
      { name: "Saharsa", code: "92" },
      { name: "Samastipur", code: "93" },
      { name: "Saran", code: "94" },
      { name: "Sheikhpura", code: "95" },
      { name: "Sheohar", code: "96" },
      { name: "Sitamarhi", code: "97" },
      { name: "Siwan", code: "98" },
      { name: "Supaul", code: "99" },
      { name: "Vaishali", code: "100" },
    ],
  },
  {
    name: "Chandigarh",
    code: "6",
    cities: [{ name: "Chandigarh", code: "101" }],
  },
  {
    name: "Chhattisgarh",
    code: "7",
    cities: [
      { name: "Baster", code: "102" },
      { name: "Bilaspur", code: "103" },
      { name: "Dantewada", code: "104" },
      { name: "Dhamtari", code: "105" },
      { name: "Durg", code: "106" },
      { name: "Janjgir-Champa", code: "107" },
      { name: "Jashpur", code: "108" },
      { name: "Kanker", code: "109" },
      { name: "Kawardha", code: "110" },
      { name: "Korba", code: "111" },
      { name: "Koriya", code: "112" },
      { name: "Mahasamund", code: "113" },
      { name: "Raigarh", code: "114" },
      { name: "Raipur", code: "115" },
      { name: "Rajnandgaon", code: "116" },
      { name: "Surguja", code: "117" },
    ],
  },
  {
    name: "Dadra & Nagar Haveli",
    code: "8",
    cities: [{ name: "Dadra & Nagar Haveli", code: "118" }],
  },
  {
    name: "Daman & Diu",
    code: "9",
    cities: [
      { name: "Daman", code: "119" },
      { name: "Diu", code: "120" },
    ],
  },
  {
    name: "Goa",
    code: "10",
    cities: [
      { name: "North Goa", code: "121" },
      { name: "South Goa", code: "122" },
    ],
  },
  {
    name: "Gujarat",
    code: "11",
    cities: [
      { name: "Ahmadabad", code: "123" },
      { name: "Amreli", code: "124" },
      { name: "Anand", code: "125" },
      { name: "Banas Kantha", code: "126" },
      { name: "Bharuch", code: "127" },
      { name: "Bhavnagar", code: "128" },
      { name: "Dohad", code: "129" },
      { name: "Gandhinagar", code: "130" },
      { name: "Jamnagar", code: "131" },
      { name: "Junagadh", code: "132" },
      { name: "Kachchh", code: "133" },
      { name: "Kheda", code: "134" },
      { name: "Mahesana", code: "135" },
      { name: "Narmada", code: "136" },
      { name: "Navsari", code: "137" },
      { name: "Panch Mahals", code: "138" },
      { name: "Patan", code: "139" },
      { name: "Porbandar", code: "140" },
      { name: "Rajkot", code: "141" },
      { name: "Sabar Kantha", code: "142" },
      { name: "Surat", code: "143" },
      { name: "Surendranagar", code: "144" },
      { name: "The Dangs", code: "145" },
      { name: "Vadodara", code: "146" },
      { name: "Valsad", code: "147" },
    ],
  },
  {
    name: "Haryana",
    code: "12",
    cities: [
      { name: "Ambala", code: "148" },
      { name: "Bhiwani", code: "149" },
      { name: "Faridabad", code: "150" },
      { name: "Fatehabad", code: "151" },
      { name: "Gurgaon", code: "152" },
      { name: "Hisar", code: "153" },
      { name: "Jhajjar", code: "154" },
      { name: "Jind", code: "155" },
      { name: "Kaithal", code: "156" },
      { name: "Karnal", code: "157" },
      { name: "Kurukshetra", code: "158" },
      { name: "Mahendragarh", code: "159" },
      { name: "Panchkula", code: "160" },
      { name: "Panipat", code: "161" },
      { name: "Rewari", code: "162" },
      { name: "Rohtak", code: "163" },
      { name: "Sirsa", code: "164" },
      { name: "Sonipat", code: "165" },
      { name: "Yamunanagar", code: "166" },
    ],
  },
  {
    name: "Himachal Pradesh",
    code: "13",
    cities: [
      { name: "Bilaspur", code: "167" },
      { name: "Chamba", code: "168" },
      { name: "Hamirpur", code: "169" },
      { name: "Kangra", code: "170" },
      { name: "Kinnaur", code: "171" },
      { name: "Kullu", code: "172" },
      { name: "Lahul & Spiti", code: "173" },
      { name: "Mandi", code: "174" },
      { name: "Shimla", code: "175" },
      { name: "Sirmaur", code: "176" },
      { name: "Solan", code: "177" },
      { name: "Una", code: "178" },
    ],
  },
  {
    name: "Jammu & Kashmir",
    code: "14",
    cities: [
      { name: "Anantnag", code: "179" },
      { name: "Baramula", code: "180" },
      { name: "Jammu", code: "181" },
      { name: "Kathua", code: "182" },
      { name: "Leh(Ladakh)", code: "183" },
      { name: "Punch", code: "184" },
      { name: "Srinagar", code: "185" },
      { name: "Udhampur", code: "186" },
    ],
  },
  {
    name: "Jharkhand",
    code: "15",
    cities: [
      { name: "Bokaro", code: "187" },
      { name: "Chatra", code: "188" },
      { name: "Deoghar", code: "189" },
      { name: "Dhanbad", code: "190" },
      { name: "Dumka", code: "191" },
      { name: "Garhwa", code: "192" },
      { name: "Giridih", code: "193" },
      { name: "Godda", code: "194" },
      { name: "Gumla", code: "195" },
      { name: "Hazaribag", code: "196" },
      { name: "Jamtara", code: "197" },
      { name: "Kodarma", code: "198" },
      { name: "Latehar", code: "199" },
      { name: "Lohardaga", code: "200" },
      { name: "Pachim Sionghum", code: "201" },
      { name: "Pakaur", code: "202" },
      { name: "Palamu", code: "203" },
      { name: "Purbi Singhbhum", code: "204" },
      { name: "Ranchi", code: "205" },
      { name: "Sahibganj", code: "206" },
      { name: "Saraikela", code: "207" },
      { name: "Simdega", code: "208" },
    ],
  },
  {
    name: "Karnataka",
    code: "16",
    cities: [
      { name: "Bagalkot", code: "209" },
      { name: "Bangalore", code: "210" },
      { name: "Belgaum", code: "212" },
      { name: "Bellary", code: "213" },
      { name: "Bidar", code: "214" },
      { name: "Bijapur", code: "215" },
      { name: "Chamrajnagar", code: "216" },
      { name: "Chikmagalur", code: "217" },
      { name: "Chitradurga", code: "218" },
      { name: "Dakshina Kannada", code: "219" },
      { name: "Davangere", code: "220" },
      { name: "Dharwad", code: "221" },
      { name: "Gadag", code: "222" },
      { name: "Gulbarga", code: "223" },
      { name: "Hassan", code: "224" },
      { name: "Haveri", code: "225" },
      { name: "Kodagu", code: "226" },
      { name: "Kolar", code: "227" },
      { name: "Koppal", code: "228" },
      { name: "Mandya", code: "229" },
      { name: "Mysore", code: "230" },
      { name: "Raichur", code: "231" },
      { name: "Shimoga", code: "232" },
      { name: "Tumkur", code: "233" },
      { name: "Udupi", code: "234" },
      { name: "Uttara Kannada", code: "235" },
    ],
  },
  {
    name: "Kerala",
    code: "17",
    cities: [
      { name: "Alappuzha", code: "236" },
      { name: "Ernakulam", code: "237" },
      { name: "Idukki", code: "238" },
      { name: "Kannur", code: "239" },
      { name: "Kasaragod", code: "240" },
      { name: "Kollam", code: "241" },
      { name: "Kottayam", code: "242" },
      { name: "Kozhikode", code: "243" },
      { name: "Malappuram", code: "244" },
      { name: "Palakkad", code: "245" },
      { name: "Pathanamthitta", code: "246" },
      { name: "Thiruvananthapuram", code: "247" },
      { name: "Thrissur", code: "248" },
      { name: "Wayanad", code: "249" },
    ],
  },
  {
    name: "Lakshadweep",
    code: "18",
    cities: [{ name: "Lakshdweep", code: "250" }],
  },
  {
    name: "Madhya Pradesh",
    code: "19",
    cities: [
      { name: "Anuppur", code: "251" },
      { name: "Ashok Nagar", code: "252" },
      { name: "Balaghat", code: "253" },
      { name: "Barwani", code: "254" },
      { name: "Betul", code: "255" },
      { name: "Bhind", code: "256" },
      { name: "Bhopal", code: "257" },
      { name: "Burhanpur", code: "258" },
      { name: "Chhatarpur", code: "259" },
      { name: "Chhindwara", code: "260" },
      { name: "Damoh", code: "261" },
      { name: "Datia", code: "262" },
      { name: "Dewas", code: "263" },
      { name: "Dhar", code: "264" },
      { name: "Dindori", code: "265" },
      { name: "East Nimar", code: "266" },
      { name: "Guna", code: "267" },
      { name: "Gwalior", code: "268" },
      { name: "Harda", code: "269" },
      { name: "Hoshangabad", code: "270" },
      { name: "Indore", code: "271" },
      { name: "Jabalpur", code: "272" },
      { name: "Jhabua", code: "273" },
      { name: "Katni", code: "274" },
      { name: "Mandla", code: "275" },
      { name: "Mandsaur", code: "276" },
      { name: "Morena", code: "277" },
      { name: "Narsimhapur", code: "278" },
      { name: "Neemuch", code: "279" },
      { name: "Panna", code: "280" },
      { name: "Raisen", code: "281" },
      { name: "Rajgarh", code: "282" },
      { name: "Ratlam", code: "283" },
      { name: "Rewa", code: "284" },
      { name: "Sagar", code: "285" },
      { name: "Satna", code: "286" },
      { name: "Sehore", code: "287" },
      { name: "Seoni", code: "288" },
      { name: "Shahdol", code: "289" },
      { name: "Shajapur", code: "290" },
      { name: "Sheopur", code: "291" },
      { name: "Shivpuri", code: "292" },
      { name: "Sidhi", code: "293" },
      { name: "Tikamgarh", code: "294" },
      { name: "Ujjain", code: "295" },
      { name: "Umaria", code: "296" },
      { name: "Vidisha", code: "297" },
      { name: "West Nimar", code: "298" },
    ],
  },
  {
    name: "Maharashtra",
    code: "20",
    cities: [
      { name: "Ahmadnagar", code: "299" },
      { name: "Akola", code: "300" },
      { name: "Amravati", code: "301" },
      { name: "Aurangabad", code: "302" },
      { name: "Bhandara", code: "303" },
      { name: "Bid", code: "304" },
      { name: "Buldana", code: "305" },
      { name: "Chandrapur", code: "306" },
      { name: "Dhule", code: "307" },
      { name: "Gadchiroli", code: "308" },
      { name: "Gondiya", code: "309" },
      { name: "Hingoli", code: "310" },
      { name: "Jalgaon", code: "311" },
      { name: "Jalna", code: "312" },
      { name: "Kolhapur", code: "313" },
      { name: "Latur", code: "314" },
      { name: "Mumbai", code: "315" },
      { name: "Mumbai (Suburban)", code: "316" },
      { name: "Nagpur", code: "317" },
      { name: "Nanded", code: "318" },
      { name: "Nandurbar", code: "319" },
      { name: "Nashik", code: "320" },
      { name: "Osmanabad", code: "321" },
      { name: "Parbhani", code: "322" },
      { name: "Pune", code: "323" },
      { name: "Raigarh", code: "324" },
      { name: "Ratnagiri", code: "325" },
      { name: "Sangli", code: "326" },
      { name: "Satara", code: "327" },
      { name: "Sindhudurg", code: "328" },
      { name: "Solapur", code: "329" },
      { name: "Thane", code: "330" },
      { name: "Wardha", code: "331" },
      { name: "Washim", code: "332" },
      { name: "Yavatmal", code: "333" },
    ],
  },
  {
    name: "Manipur",
    code: "21",
    cities: [
      { name: "Bishnupur", code: "334" },
      { name: "Chandel", code: "335" },
      { name: "Churachandpur", code: "336" },
      { name: "Imphal East", code: "337" },
      { name: "Senapati", code: "338" },
      { name: "Tamenglong", code: "339" },
      { name: "Thoubal", code: "340" },
      { name: "Ukhrul", code: "341" },
    ],
  },
  {
    name: "Meghalaya",
    code: "22",
    cities: [
      { name: "East Garo Hills", code: "342" },
      { name: "East Khasi Hills", code: "343" },
      { name: "Jaintia Hills", code: "344" },
      { name: "Ri Bhoi", code: "345" },
      { name: "South Garo Hills", code: "346" },
      { name: "West Garo Hills", code: "347" },
      { name: "West Khasi Hills", code: "348" },
    ],
  },
  {
    name: "Mizoram",
    code: "23",
    cities: [
      { name: "Aizawl", code: "349" },
      { name: "Champhai", code: "350" },
      { name: "Kolasib", code: "351" },
      { name: "Lunglei", code: "352" },
      { name: "Mamit", code: "353" },
      { name: "Saiha", code: "354" },
      { name: "Serchhip", code: "355" },
    ],
  },
  {
    name: "Nagaland",
    code: "24",
    cities: [
      { name: "Dimapur", code: "356" },
      { name: "Kohima", code: "357" },
      { name: "Mokokchung", code: "358" },
      { name: "Mon", code: "359" },
      { name: "Phek", code: "360" },
      { name: "Tuensang", code: "361" },
      { name: "Wokha", code: "362" },
      { name: "Zunheboto", code: "363" },
    ],
  },
  {
    name: "Orissa",
    code: "25",
    cities: [
      { name: "Anugul", code: "364" },
      { name: "Balangir", code: "365" },
      { name: "Baleshwar", code: "366" },
      { name: "Bargarh", code: "367" },
      { name: "Baudh", code: "368" },
      { name: "Bhadrak", code: "370" },
      { name: "Cuttack", code: "371" },
      { name: "Debagarh", code: "372" },
      { name: "Dhenkanal", code: "373" },
      { name: "Gajapati", code: "374" },
      { name: "Ganjam", code: "375" },
      { name: "Jagatsinghapur", code: "376" },
      { name: "Jajapur", code: "377" },
      { name: "Jharsuguda", code: "378" },
      { name: "Kalahandi", code: "379" },
      { name: "Kandhamal", code: "380" },
      { name: "Kendrapara", code: "381" },
      { name: "Kendujhar", code: "382" },
      { name: "Khordha", code: "383" },
      { name: "Koraput", code: "384" },
      { name: "Malkangiri", code: "385" },
      { name: "Mayurbhanj", code: "386" },
      { name: "Nabarangapur", code: "387" },
      { name: "Nayagarh", code: "388" },
      { name: "Nuapada", code: "389" },
      { name: "Puri", code: "390" },
      { name: "Rayagada", code: "391" },
      { name: "Sambalpur", code: "392" },
      { name: "Sundargarh", code: "393" },
    ],
  },
  {
    name: "Pondicherry",
    code: "26",
    cities: [
      { name: "Karaikal", code: "394" },
      { name: "Pondicherry", code: "395" },
      { name: "Yanam", code: "396" },
    ],
  },
  {
    name: "Punjab",
    code: "27",
    cities: [
      { name: "Amritsar", code: "397" },
      { name: "Bathinda", code: "398" },
      { name: "Faridkot", code: "399" },
      { name: "Fatehgarh Sahib", code: "400" },
      { name: "Firozpur", code: "401" },
      { name: "Gurdaspur", code: "402" },
      { name: "Hoshiarpur", code: "403" },
      { name: "Jalandhar", code: "404" },
      { name: "Kapurthala", code: "405" },
      { name: "Ludhiana", code: "406" },
      { name: "Mansa", code: "407" },
      { name: "Moga", code: "408" },
      { name: "Muktsar", code: "409" },
      { name: "Nawanshahr", code: "410" },
      { name: "Patiala", code: "411" },
      { name: "Rupnagar", code: "412" },
      { name: "Sangrur", code: "413" },
    ],
  },
  {
    name: "Rajasthan",
    code: "28",
    cities: [
      { name: "Ajmer", code: "414" },
      { name: "Alwar", code: "415" },
      { name: "Banswara", code: "416" },
      { name: "Baran", code: "417" },
      { name: "Barmer", code: "418" },
      { name: "Bharatpur", code: "419" },
      { name: "Bhilwara", code: "420" },
      { name: "Bikaner", code: "421" },
      { name: "Bundi", code: "422" },
      { name: "Chittaurgarh", code: "423" },
      { name: "Churu", code: "424" },
      { name: "Dausa", code: "425" },
      { name: "Dhaulpur", code: "426" },
      { name: "Dungarpur", code: "427" },
      { name: "Ganganagar", code: "428" },
      { name: "Hanumangarh", code: "429" },
      { name: "Jaipur", code: "430" },
      { name: "Jaisalmer", code: "431" },
      { name: "Jalor", code: "432" },
      { name: "Jhalawar", code: "433" },
      { name: "Jhunjhunun", code: "434" },
      { name: "Jodhpur", code: "435" },
      { name: "Karauli", code: "436" },
      { name: "Kota", code: "437" },
      { name: "Nagaur", code: "438" },
      { name: "Pali", code: "439" },
      { name: "Rajsamand", code: "440" },
      { name: "Sawai Madhopur", code: "441" },
      { name: "Sikar", code: "442" },
      { name: "Sirohi", code: "443" },
      { name: "Tonk", code: "444" },
      { name: "Udaipur", code: "445" },
    ],
  },
  {
    name: "Sikkim",
    code: "29",
    cities: [
      { name: "East", code: "446" },
      { name: "North", code: "447" },
      { name: "South", code: "448" },
      { name: "West", code: "449" },
    ],
  },
  {
    name: "Tamil Nadu",
    code: "30",
    cities: [
      { name: "Chennai", code: "450" },
      { name: "Coimbatore", code: "451" },
      { name: "Cuddalore", code: "452" },
      { name: "Dharmapuri", code: "453" },
      { name: "Dindigul", code: "454" },
      { name: "Erode", code: "455" },
      { name: "Kancheepuram", code: "456" },
      { name: "Kanniyakumari", code: "457" },
      { name: "Karur", code: "458" },
      { name: "Krishnagiri", code: "459" },
      { name: "Madurai", code: "460" },
      { name: "Nagapattinam", code: "461" },
      { name: "Namakkal", code: "462" },
      { name: "Perambalur", code: "463" },
      { name: "Pudukkottai", code: "464" },
      { name: "Ramanathapuram", code: "465" },
      { name: "Salem", code: "466" },
      { name: "Sivaganga", code: "467" },
      { name: "Thanjavur", code: "468" },
      { name: "The Nilgiris", code: "469" },
      { name: "Theni", code: "470" },
      { name: "Thiruvallur", code: "471" },
      { name: "Thiruvarur", code: "472" },
      { name: "Thoothukkudi", code: "473" },
      { name: "Tiruchirappalli", code: "474" },
      { name: "Tirunelveli", code: "475" },
      { name: "Tiruvannamalai", code: "476" },
      { name: "Vellore", code: "477" },
      { name: "Viluppuram", code: "478" },
      { name: "Virudhunagar", code: "479" },
    ],
  },
  {
    name: "Tripura",
    code: "31",
    cities: [
      { name: "Dhalai", code: "480" },
      { name: "North Tripura", code: "481" },
      { name: "South Tripura", code: "482" },
      { name: "West Tripura", code: "483" },
    ],
  },
  {
    name: "Uttar Pradesh",
    code: "32",
    cities: [
      { name: "Agra", code: "484" },
      { name: "Aligarh", code: "485" },
      { name: "Allahabad", code: "486" },
      { name: "Ambedkar Nagar", code: "487" },
      { name: "Auraiya", code: "488" },
      { name: "Azamgarh", code: "489" },
      { name: "Baghpat", code: "490" },
      { name: "Bahraich", code: "491" },
      { name: "Ballia", code: "492" },
      { name: "Balrampur", code: "493" },
      { name: "Banda", code: "494" },
      { name: "Barabanki", code: "495" },
      { name: "Bareilly", code: "496" },
      { name: "Basti", code: "497" },
      { name: "Bijnor", code: "498" },
      { name: "Budaun", code: "499" },
      { name: "Bulandshahr", code: "500" },
      { name: "Chandauli", code: "501" },
      { name: "Chitrakoot", code: "502" },
      { name: "Deoria", code: "503" },
      { name: "Etah", code: "504" },
      { name: "Etawah", code: "505" },
      { name: "Faizabad", code: "506" },
      { name: "Farrukhabad", code: "507" },
      { name: "Fatehpur", code: "508" },
      { name: "Firozabad", code: "509" },
      { name: "Gautam Buddha Nagar", code: "510" },
      { name: "Ghaziabad", code: "511" },
      { name: "Ghazipur", code: "512" },
      { name: "Gonda", code: "513" },
      { name: "Gorakhpur", code: "514" },
      { name: "Hamirpur", code: "515" },
      { name: "Hardoi", code: "516" },
      { name: "Hathras", code: "517" },
      { name: "Jalaun", code: "518" },
      { name: "Jaunpur", code: "519" },
      { name: "Jhansi", code: "520" },
      { name: "Jyotiba Phule Nagar", code: "521" },
      { name: "Kannauj", code: "522" },
      { name: "Kanpur Dehat", code: "523" },
      { name: "Kanpur Nagar", code: "524" },
      { name: "Kaushambi", code: "525" },
      { name: "Kheri", code: "526" },
      { name: "Kushinagar", code: "527" },
      { name: "Lalitpur", code: "528" },
      { name: "Lucknow", code: "529" },
      { name: "Maharajganj", code: "530" },
      { name: "Mahoba", code: "531" },
      { name: "Mainpuri", code: "532" },
      { name: "Mathura", code: "533" },
      { name: "Mau", code: "534" },
      { name: "Meerut", code: "535" },
      { name: "Mirzapur", code: "536" },
      { name: "Moradabad", code: "537" },
      { name: "Muzaffarnagar", code: "538" },
      { name: "Pilibhit", code: "539" },
      { name: "Pratapgarh", code: "540" },
      { name: "Rae Bareli", code: "541" },
      { name: "Rampur", code: "542" },
      { name: "Saharanpur", code: "543" },
      { name: "Sant Kabir Nagar", code: "544" },
      { name: "Sant Ravidas Nagar", code: "545" },
      { name: "Shahjahanpur", code: "546" },
      { name: "Shrawasti", code: "547" },
      { name: "Siddharthnagar", code: "548" },
      { name: "Sitapur", code: "549" },
      { name: "Sonbhadra", code: "550" },
      { name: "Sultanpur", code: "551" },
      { name: "Unnao", code: "552" },
      { name: "Varanasi", code: "553" },
    ],
  },
  {
    name: "Uttaranchal",
    code: "33",
    cities: [
      { name: "Almora", code: "554" },
      { name: "Bageshwar", code: "555" },
      { name: "Chamoli", code: "556" },
      { name: "Champawat", code: "557" },
      { name: "Dehradun", code: "558" },
      { name: "Garhwal", code: "559" },
      { name: "Hardwar", code: "560" },
      { name: "Nainital", code: "561" },
      { name: "Pithoragarh", code: "562" },
      { name: "Rudraprayag", code: "563" },
      { name: "Tehri Garhwal", code: "564" },
      { name: "Udham Singh Nagar", code: "565" },
      { name: "Uttarkashi", code: "566" },
    ],
  },
  {
    name: "West Bengal",
    code: "34",
    cities: [
      { name: "Bankura", code: "567" },
      { name: "Barddhaman", code: "568" },
      { name: "Birbhum", code: "569" },
      { name: "Dakshin Dinajpur", code: "570" },
      { name: "Darjiling", code: "571" },
      { name: "Haora", code: "572" },
      { name: "Hugli", code: "573" },
      { name: "Jalpaiguri", code: "574" },
      { name: "Koch Bihar", code: "575" },
      { name: "Kolkata", code: "576" },
      { name: "Maldah", code: "577" },
      { name: "Murshidabad", code: "578" },
      { name: "Nadia", code: "579" },
      { name: "North Twenty Four Pargana", code: "580" },
      { name: "Paschim Medinapur", code: "581" },
      { name: "Purba Medinapur", code: "582" },
      { name: "Puruliya", code: "583" },
      { name: "South Twenty Four Pargan", code: "584" },
      { name: "Uttar Dinajpur", code: "585" },
    ],
  },
]

export const getCitiesForState = (stateName: string): LocationCity[] => {
  const entry = LOCATION_STATES.find(s => s.name === stateName)
  return entry ? entry.cities : []
}

export const getStateCode = (stateName: string): string => {
  const entry = LOCATION_STATES.find(s => s.name === stateName)
  return entry ? entry.code : ""
}

export const getCityCode = (stateName: string, cityName: string): string => {
  const city = getCitiesForState(stateName).find(c => c.name === cityName)
  return city ? city.code : ""
}

export const getStateNameByCode = (stateCode: string): string => {
  const entry = LOCATION_STATES.find(s => s.code === stateCode)
  return entry ? entry.name : ""
}

// City codes are unique across the whole dataset (not just within a state),
// so this can resolve a city's display name from its code alone - useful
// when hydrating a persisted {city,state,bufferZone} row (which stores
// codes) back into editable dropdown selections (which need names).
export const getCityNameByCode = (cityCode: string): string => {
  for (const state of LOCATION_STATES) {
    const city = state.cities.find(c => c.code === cityCode)
    if (city) return city.name
  }
  return ""
}

export interface IciciCbdcLocationRow {
  state: string
  city: string
  bufferZone: string
}

/**
 * Converts editable location rows (holding human-readable state/city
 * display names, as chosen via the dropdowns) into the {city,state,
 * bufferZone} shape the ICICI CBDC Create/Update Rule API expects - where
 * `city`/`state` hold numeric codes, not names. Rows with an unresolved
 * state/city (e.g. left blank) still pass through with empty code strings -
 * no validation is applied here, matching the rest of this integration.
 */
export const toIciciCbdcLocationPayload = (
  rows: IciciCbdcLocationRow[],
): Array<{ city: string; state: string; bufferZone: string }> => {
  return rows.map(row => ({
    city: getCityCode(row.state, row.city),
    state: getStateCode(row.state),
    bufferZone: String(row.bufferZone ?? ""),
  }))
}

/**
 * Reverses toIciciCbdcLocationPayload - converts persisted {city,state,
 * bufferZone} rows (numeric codes) back into editable rows with
 * human-readable state/city names, for hydrating the edit form.
 */
export const fromIciciCbdcLocationPayload = (
  rows: Array<{ city: string; state: string; bufferZone: string }>,
): IciciCbdcLocationRow[] => {
  return rows.map(row => ({
    state: getStateNameByCode(row.state),
    city: getCityNameByCode(row.city),
    bufferZone: String(row.bufferZone ?? ""),
  }))
}
